import { CoreTextView, type TCoreTextStyleProps } from '@core/widgets';
import { useApp, ConditionalRender } from '@libs/core';
import { TCommonDataObj, THandleClick, TWidgetCommonOption } from '@libs/types';
import React, { useState } from 'react';
import { TConsumerItem, TListDataMessages } from '../../types/data';
import { TransactionStatusEnum } from '../../types/enums';
import { TListOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import { RowTransaction } from '../UI/Row/RowTransaction';
import styles from './styles/styles.module.css';
import { resolveArguments } from './utils/transactionUtils';

const SEPARATOR_TEXT_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.subheader,
    main: 'var(--color-grey_72_lc)',
    textTransform: 'uppercase',
    margin: {
        top: 16,
        bottom: 16,
        right: 24,
        left: 24,
    },
};

type TListItemsProps = {
    items: Array<TConsumerItem>;
    wid: string;
    messages: TListDataMessages;
    handleClick: THandleClick;
} & Pick<TListOption, 'state'> &
    Pick<TWidgetCommonOption, 'parentStyleNames'>;
type SortedItem = {
    separator?: string;
} & TConsumerItem;

function TransactionsListItems({ wid, items, messages, handleClick, parentStyleNames, state = 'FULL' }: TListItemsProps) {
    const { language } = useApp();
    const [currentDate] = useState(new Date());
    const [yesterdayDate] = useState(new Date(new Date().setDate(currentDate.getDate() - 1)));

    const sortTransactions = (transactions: TConsumerItem[]): SortedItem[] => {
        const sortedArr = Array.isArray(transactions)
            ? [...transactions].sort((a, b) => {
                  if (a?.status === TransactionStatusEnum.AWAITING && b?.status !== TransactionStatusEnum.AWAITING) {
                      return -1;
                  } else if (a?.status !== TransactionStatusEnum.AWAITING && b?.status === TransactionStatusEnum.AWAITING) {
                      return 1;
                  } else {
                      const dateA = a?.date instanceof Date ? a?.date : new Date(a?.date as string);
                      const dateB = b?.date instanceof Date ? b?.date : new Date(b?.date as string);
                      return dateB.getTime() - dateA.getTime();
                  }
              })
            : [];
        const result = sortedArr.reduce(
            (acc, item) => {
                let separator: string | undefined;
                if (item?.status === TransactionStatusEnum.AWAITING) {
                    if (!acc.awaitingAdded) {
                        acc.awaitingAdded = true;
                        separator = messages?.awaiting;
                    }
                } else {
                    const date = item?.date as Date;
                    if (date?.getFullYear?.()) {
                        const month = date.getMonth().toString();
                        const monthAndYear = `${month}_${date.getFullYear()}`;
                        const monthAndDay = `${month}_${date.getDate()}`;
                        if (date.toDateString() === currentDate.toDateString()) {
                            if (!acc.todayAdded) {
                                acc.todayAdded = true;
                                separator = messages?.today;
                            }
                        } else if (date.toDateString() === yesterdayDate.toDateString()) {
                            if (!acc.yesterdayAdded) {
                                acc.yesterdayAdded = true;
                                separator = messages?.yesterday;
                            }
                        } else if (currentDate.getFullYear() === date.getFullYear() && currentDate.getMonth() === date.getMonth()) {
                            if (!acc.currentMonthAdded[monthAndDay]) {
                                acc.currentMonthAdded[monthAndDay] = true;
                                separator = resolveArguments(messages?.monthList?.[date.getMonth()], date.getDate());
                            }
                        } else if (currentDate.getFullYear() === date.getFullYear()) {
                            if (!acc.currentYearAdded[monthAndYear]) {
                                acc.currentYearAdded[monthAndYear] = true;
                                separator = date.toLocaleString(language ?? 'default', { month: 'long' });
                            }
                        } else if (!acc.moreThanYearAdded[monthAndYear]) {
                            acc.moreThanYearAdded[monthAndYear] = true;
                            separator = `${date.toLocaleString(language ?? 'default', { month: 'long' })} ${date.getFullYear()}`;
                        }
                    }
                }
                acc.newArr.push({ ...item, separator });
                return acc;
            },
            {
                newArr: [] as SortedItem[],
                awaitingAdded: false,
                todayAdded: false,
                yesterdayAdded: false,
                currentMonthAdded: {} as TCommonDataObj,
                currentYearAdded: {} as TCommonDataObj,
                moreThanYearAdded: {} as TCommonDataObj,
            },
        );
        return result.newArr;
    };

    if (items?.length > 0) {
        return sortTransactions(items).map((item: SortedItem, index) => {
            const isClickable = item.clickable !== false;
            return (
                <div className={styles.transactionListItemWrapper} key={`${index}_${item?.id}`}>
                    <ConditionalRender condition={state === 'FULL'}>
                        <div className={styles.transactionListItemSeparatorWrapper}>
                            <ConditionalRender condition={!!item.separator}>
                                <CoreTextView
                                    text={item.separator}
                                    styleProps={SEPARATOR_TEXT_STYLE_PROPS}
                                    parentStyleNames={parentStyleNames}
                                    styleName="transactionsListDateHeader"
                                />
                            </ConditionalRender>
                        </div>
                    </ConditionalRender>
                    <RowTransaction
                        as={isClickable ? 'button' : 'div'}
                        parentStyleNames={[...(parentStyleNames ?? []), 'transactionRow']}
                        item={item}
                        handleClick={isClickable ? () => handleClick(wid, item) : undefined}
                    />
                </div>
            );
        });
    }
    return null;
}

export default TransactionsListItems;
